import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { sectionReveal, heroReveal } from "@/lib/motion";
import { Cookie, Mail, ArrowLeft } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PrintButton } from "@/components/PrintButton";

const LAST_UPDATED = "June 20, 2026";
const CONTACT_EMAIL = "emergo.support@gmail.com";

const Block = ({ title, children }) => (
  <motion.section
    {...sectionReveal}
    className="space-y-3"
  >
    <h2 className="font-extrabold tracking-tight text-2xl md:text-[1.6rem] leading-tight text-ink">{title}</h2>
    <div className="space-y-3 text-ink-2 leading-relaxed text-base">{children}</div>
  </motion.section>
);

export default function CookieNotice() {
  return (
    <div className="min-h-screen bg-white text-ink antialiased" data-testid="cookie-notice-page">
      <Header />

      <main className="mx-auto max-w-3xl px-6">
        <motion.div
          {...heroReveal}
          className="pt-16 pb-12 border-b border-gray-200"
        >
          <Link to="/" data-testid="back-to-home-link" className="print:hidden inline-flex items-center gap-2 text-sm text-gray-500 hover:text-brand transition-colors mb-8">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
            Back to home
          </Link>
          <p className="text-xs uppercase tracking-[0.08em] font-bold text-brand mb-4">Legal</p>
          <h1 className="font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ink" data-testid="page-title">
            Cookie Notice
          </h1>
          <p className="mt-6 text-lg text-ink-2 max-w-xl leading-relaxed">
            A short explanation of how Emergo uses cookies and similar technologies.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <p className="text-sm text-gray-500" data-testid="last-updated">Last updated: {LAST_UPDATED}</p>
            <PrintButton />
          </div>
        </motion.div>

        <div className="py-14 space-y-12">
          <Block title="What Are Cookies?">
            <p>
              Cookies are small text files that a website or app stores on your device. They are widely used to make
              digital products work, to keep you signed in, and to remember your preferences between visits.
            </p>
          </Block>

          <Block title="How Emergo Uses Cookies">
            <p>
              Emergo uses only the cookies and similar storage that are necessary for the app to function. These are
              strictly essential cookies, used to:
            </p>
            <ul className="list-disc pl-5 space-y-2 marker:text-brand">
              <li>Keep you securely signed in to your account during your session.</li>
              <li>Remember your basic settings and preferences within the app.</li>
              <li>Keep the app secure and working correctly.</li>
            </ul>
          </Block>

          <Block title="No Tracking or Advertising Cookies">
            <p>
              We do not use cookies for advertising, and we do not use third-party analytics or tracking cookies.
              Emergo does not share cookie data with external services, and we do not build advertising profiles about
              you.
            </p>
          </Block>

          <Block title="Managing Cookies">
            <p>
              You can control or delete cookies through your browser or device settings at any time. Because Emergo
              relies only on essential cookies, disabling them may prevent parts of the app — such as staying signed in
              — from working as expected.
            </p>
          </Block>

          <Block title="Changes to This Notice">
            <p>
              We may update this Cookie Notice from time to time. When we do, we will revise the "last updated" date at
              the top of this page.
            </p>
          </Block>

          <Block title="Contact Us">
            <p>If you have any questions about how Emergo uses cookies, please contact us at:</p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="contact-email-link"
              className="inline-flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-5 py-4 text-ink transition-colors hover:border-brand group"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-soft text-brand transition-transform group-hover:scale-105">
                <Mail className="h-[18px] w-[18px]" strokeWidth={1.75} />
              </span>
              <span className="font-medium">{CONTACT_EMAIL}</span>
            </a>
          </Block>
        </div>
      </main>
      <Footer />
    </div>
  );
}
